'use client'

import './productsFilterBar.css'
import { useRef, useState, useCallback } from 'react'
import { useTranslations } from 'next-intl'
import HoverGradientButton from '@components/buttons/HoverGradientButton/HoverGradientButton'
import { FaceImage } from '@components/images/FaceImage/FaceImage'

// ─── Types ────────────────────────────────────────────────────────────────────

type Props = {
  categoryOptions: { value: string; label: string }[]
  categoryCounts: Record<string, number>
  categories: string[]
  search: string
  ingredients: string[]
  excludedIngredients: string[]
  priceMin: number
  priceMax: number
  productPriceRange: { min: number; max: number }
  /** Rating buckets, high → low. Only rendered when `showCompatibility` is true. */
  compatibilityOptions: { score: number; label: string }[]
  /** Raw 0–4 scores currently selected. Same state the product list filters on. */
  selectedCompatibility: number[]
  /** True only when the user has a survey — otherwise there are no scores to filter. */
  showCompatibility: boolean
  onCategoryToggle: (v: string) => void
  onSearchChange: (v: string) => void
  onIngredientsChange: (tags: string[]) => void
  onExcludedIngredientsChange: (tags: string[]) => void
  onPriceChange: (min: number, max: number) => void
  onCompatibilityToggle: (score: number) => void
  onClearAll: () => void
  locale: string
}

// ─── PriceRangeSlider ─────────────────────────────────────────────────────────

function PriceRangeSlider({
  min,
  max,
  hardMin,
  hardMax,
  onChange,
  currency,
  minLabel,
  maxLabel,
}: {
  min: number
  max: number
  hardMin: number
  hardMax: number
  onChange: (min: number, max: number) => void
  currency: string
  minLabel: string
  maxLabel: string
}) {
  const trackRef = useRef<HTMLDivElement>(null)

  const toPercent = (v: number) =>
    hardMax === hardMin ? 0 : ((v - hardMin) / (hardMax - hardMin)) * 100

  const startDrag = useCallback(
    (thumb: 'min' | 'max') => (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      const el = e.currentTarget
      el.setPointerCapture(e.pointerId)

      const move = (ev: PointerEvent) => {
        const track = trackRef.current
        if (!track) return
        const { left, width } = track.getBoundingClientRect()
        const pct = Math.max(0, Math.min(100, ((ev.clientX - left) / width) * 100))
        const raw = Math.round(hardMin + (pct / 100) * (hardMax - hardMin))
        if (thumb === 'min') {
          onChange(Math.min(raw, max - 1), max)
        } else {
          onChange(min, Math.max(raw, min + 1))
        }
      }

      const up = () => {
        el.removeEventListener('pointermove', move as EventListener)
        el.removeEventListener('pointerup', up)
      }
      el.addEventListener('pointermove', move as EventListener)
      el.addEventListener('pointerup', up)
    },
    [min, max, hardMin, hardMax, onChange],
  )

  const handleKey = (thumb: 'min' | 'max') => (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault()
      if (thumb === 'min') onChange(Math.max(hardMin, min - 1), max)
      else onChange(min, Math.max(min + 1, max - 1))
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault()
      if (thumb === 'min') onChange(Math.min(max - 1, min + 1), max)
      else onChange(min, Math.min(hardMax, max + 1))
    }
  }

  const minPct = toPercent(min)
  const maxPct = toPercent(max)

  return (
    <div className="px-1">
      <div className="flex justify-between text-xs text-black/50 mb-2">
        <span>{currency}{min}</span>
        <span>{currency}{max}</span>
      </div>
      <div ref={trackRef} className="price-range-track">
        {/* filled range */}
        <div
          className="price-range-fill"
          style={{ left: `${minPct}%`, right: `${100 - maxPct}%` }}
        />
        {/* min thumb */}
        <div
          role="slider"
          aria-label={minLabel}
          aria-valuemin={hardMin}
          aria-valuemax={max - 1}
          aria-valuenow={min}
          aria-valuetext={`${currency}${min}`}
          tabIndex={0}
          className="price-range-thumb"
          style={{ left: `${minPct}%` }}
          onPointerDown={startDrag('min')}
          onKeyDown={handleKey('min')}
        />
        {/* max thumb */}
        <div
          role="slider"
          aria-label={maxLabel}
          aria-valuemin={min + 1}
          aria-valuemax={hardMax}
          aria-valuenow={max}
          aria-valuetext={`${currency}${max}`}
          tabIndex={0}
          className="price-range-thumb"
          style={{ left: `${maxPct}%` }}
          onPointerDown={startDrag('max')}
          onKeyDown={handleKey('max')}
        />
      </div>
    </div>
  )
}

// ─── IngredientTagInput ───────────────────────────────────────────────────────

function IngredientTagInput({
  tags,
  placeholder,
  onChange,
  removeLabel,
}: {
  tags: string[]
  placeholder: string
  onChange: (next: string[]) => void
  removeLabel: (tag: string) => string
}) {
  const [inputVal, setInputVal] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const commit = (raw: string) => {
    const val = raw.trim()
    if (val && !tags.includes(val)) onChange([...tags, val])
    setInputVal('')
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      commit(inputVal)
    }
    if (e.key === 'Backspace' && inputVal === '' && tags.length > 0) {
      onChange(tags.slice(0, -1))
    }
  }

  return (
    <div
      className="ingredient-tag-input"
      onClick={() => inputRef.current?.focus()}
    >
      {tags.map((tag) => (
        <span
          key={tag}
          className="ingredient-tag"
        >
          {tag}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onChange(tags.filter((t) => t !== tag)) }}
            aria-label={removeLabel(tag)}
            className="ingredient-tag-remove"
          >
            ×
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        value={inputVal}
        onChange={(e) => setInputVal(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => { if (inputVal.trim()) commit(inputVal) }}
        placeholder={tags.length === 0 ? placeholder : ''}
        className="ingredient-tag-input-field"
      />
    </div>
  )
}

// ─── ProductsFilterBar ────────────────────────────────────────────────────────

export function ProductsFilterBar({
  categoryOptions,
  categoryCounts,
  categories,
  search,
  ingredients,
  excludedIngredients,
  priceMin,
  priceMax,
  productPriceRange,
  compatibilityOptions,
  selectedCompatibility,
  showCompatibility,
  onCategoryToggle,
  onSearchChange,
  onIngredientsChange,
  onExcludedIngredientsChange,
  onPriceChange,
  onCompatibilityToggle,
  onClearAll,
  locale,
}: Props) {
  const t = useTranslations('productsPage')
  const currency = locale === 'it' ? '€' : '$'

  const hasActiveFilters =
    search ||
    categories.length > 0 ||
    ingredients.length > 0 ||
    excludedIngredients.length > 0 ||
    selectedCompatibility.length > 0 ||
    priceMin !== productPriceRange.min ||
    priceMax !== productPriceRange.max

  return (
    <div id="products-filter-bar" className="flex flex-col gap-5">
      {/* Compatibility — multi-select, exact bucket. Only when the user has a
          survey; without scores there is nothing to filter on. Mirrors the same
          selectedCompatibility state the product list filters and sorts on. */}
      {showCompatibility && (
        <div>
          <p className="text-[11px] text-black/40 uppercase tracking-wider mb-1.5">{t('filterCompatibility')}</p>
          <div className="flex flex-col gap-1.5">
            {compatibilityOptions.map((opt) => (
              <label key={opt.score} className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={selectedCompatibility.includes(opt.score)}
                  onChange={() => onCompatibilityToggle(opt.score)}
                  className="accent-orange w-3.5 h-3.5 flex-shrink-0"
                />
                <FaceImage score={opt.score} size={18} alt="" />
                <span className="text-sm text-black group-hover:text-orange transition-colors flex-1">
                  {opt.label}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Search */}
      <div>
        <p className="text-[11px] text-black/40 uppercase tracking-wider mb-1.5">{t('filterSearch')}</p>
        <input
          type="search"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t('filterSearch')}
          aria-label={t('filterSearch')}
          className="w-full bg-transparent border border-black/20 rounded-lg px-3 py-2 text-sm text-black focus:outline-none focus:border-orange transition-colors"
        />
      </div>

      {/* Category checkboxes */}
      <div>
        <p className="text-[11px] text-black/40 uppercase tracking-wider mb-1.5">{t('filterCategory')}</p>
        <div className="flex flex-col gap-1.5">
          {categoryOptions.map((opt) => (
            <label key={opt.value} className="flex items-center gap-2 cursor-pointer group">
              <input
                type="checkbox"
                checked={categories.includes(opt.value)}
                onChange={() => onCategoryToggle(opt.value)}
                className="accent-orange w-3.5 h-3.5 flex-shrink-0"
              />
              <span className="text-sm text-black group-hover:text-orange transition-colors flex-1">
                {opt.label}
              </span>
              <span className="text-xs text-black/30 tabular-nums">
                {categoryCounts[opt.value] ?? 0}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Price range */}
      {productPriceRange.min < productPriceRange.max && (
        <div>
          <p className="text-[11px] text-black/40 uppercase tracking-wider mb-2">{t('filterPriceRange')}</p>
          <PriceRangeSlider
            min={priceMin}
            max={priceMax}
            hardMin={productPriceRange.min}
            hardMax={productPriceRange.max}
            onChange={onPriceChange}
            currency={currency}
            minLabel={t('filterMinPrice')}
            maxLabel={t('filterMaxPrice')}
          />
        </div>
      )}

      {/* Ingredients */}
      <div>
        <p className="text-[11px] text-black/40 uppercase tracking-wider mb-2">{t('filterIngredients')}</p>
        <div className="flex flex-col gap-3">
          <div>
            <p className="text-xs text-black/50 mb-1">{t('filterMustHave')}</p>
            <IngredientTagInput
              tags={ingredients}
              placeholder={t('filterIngredientAdd')}
              onChange={onIngredientsChange}
              removeLabel={(tag) => t('filterRemoveIngredient', { tag })}
            />
          </div>
          <div>
            <p className="text-xs text-black/50 mb-1">{t('filterMustAvoid')}</p>
            <IngredientTagInput
              tags={excludedIngredients}
              placeholder={t('filterIngredientAdd')}
              onChange={onExcludedIngredientsChange}
              removeLabel={(tag) => t('filterRemoveIngredient', { tag })}
            />
          </div>
        </div>
      </div>

      {/* Clear all */}
      {hasActiveFilters && (
        <HoverGradientButton
          variant="outline"
          onClick={onClearAll}
          className="w-full !py-2 text-xs"
        >
          {t('filterClearAll')}
        </HoverGradientButton>
      )}
    </div>
  )
}
